import { UiSchema } from '@rjsf/utils'
import { JSONSchema7 } from 'json-schema'
import get from 'lodash/get'
import has from 'lodash/has'
import isArray from 'lodash/isArray'
import isNull from 'lodash/isNull'
import omit from 'lodash/omit'
import { call, fork, put, select, StrictEffect, takeLatest } from 'redux-saga/effects'

import {
    CHANGE_DRAWABLE_GROUP_TYPE,
    changeDrawableGroupType,
    SET_ACTIVE_DRAWABLE_GROUP,
    setActiveDrawableGroup,
} from '../../../actions/drawable'
import { getFormSchema } from '../../../api/takeoff-api'
import { materialToFormMap } from '../../../formSchemas'
import { addAdvancedSettingsToSchemaByType } from '../../../formSchemas/advanced_settings'
import { excludedFloorDepthWallTypes } from '../../../formSchemas/wall'
import { ActiveFloor } from '../../../models/activeFloor'
import { AzureFeatureFlag } from '../../../models/azureFeatureFlags'
import { Building } from '../../../models/projectBuilding'
import { IToolObject } from '../../../models/tool'
import { AzureFeatureFlagIds } from '../../../shared/constants/azure-feature-flags'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { FormTypes } from '../../../shared/constants/form'
import {
    isAzureFeatureFlagEnabled,
    prepareUiSchemaBasedOnAzureFeatureFlag,
} from '../../../shared/services/azure-feature-flag-services/azure-feature-flag-services'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import {
    selectRimboardPendingBorderIds,
    setPendingRimboardBorders,
    setRimboardAutoFloorLevel,
    setRimboardParentSettings,
} from '../../slices/2D'
import { selectActiveBuildingFromActiveFloor } from '../../slices/buildings'
import { selectDrawableActiveFloor } from '../../slices/documents'
import { selectAzureFeatureFlags } from '../../slices/flags'
import {
    activateForm,
    cacheForm,
    clearFormErrors,
    formError,
    FormsState,
    GENERAL_CACHE,
    selectFormCache,
} from '../../slices/forms'
import { Geometry, selectGeometries } from '../../slices/geometry'
import { selectActiveToolObject, setActiveToolObjectId } from '../../slices/tools'
import { FormProperties, TOOL_TYPE_ENUMS } from '../../types'
import { getClosestFloorRoofSystemSettings } from '../2D/bulkCreateDrawableLocations'

const schemasWithNewLocationModule = ['beam', 'floor_systems', 'roof_systems', 'rimboard']

const floorDepthCondition = {
    if: {
        properties: {
            type: {
                not: {
                    enum: [...excludedFloorDepthWallTypes],
                },
            },
        },
        not: {
            properties: {
                type: {
                    const: '*',
                },
            },
        },
    },
    then: {
        properties: {
            floor_depth_above: { $ref: '#/definitions/floor_depth_above' },
        },
        required: ['floor_depth_above'],
    },
}

export const addBundleAndToggleToUISchema = (uiSchema: UiSchema): UiSchema => {
    const incompleteOrder = uiSchema['ui:order'] ?? []

    return {
        ...uiSchema,
        'ui:order': [
            ...incompleteOrder,
            '*',
            'ignore_for_report',
            'bundle_name',
            'bundle_location',
            'bundle_floor_level',
            'bundle_other_floor_level',
        ],
        bundle_name: {
            'ui:placeholder': 'Select Sub-Region',
        },
        bundle_location: {
            'ui:placeholder': 'Select Sub-Region Location',
        },
        bundle_floor_level: {
            'ui:placeholder': 'Select Sub-Region Floor Level',
        },
        bundle_other_floor_level: {
            'ui:placeholder': 'Select Sub-Region Other Floor Level',
        },
        ignore_for_report: {
            'ui:title': 'Add to the material list?',
            'ui:widget': 'radio',
            'ui:options': {
                inline: true,
            },
        },
    }
}

export const addLocationModuleToUISchema = (uiSchema: UiSchema): UiSchema => {
    const incompleteOrder = uiSchema['ui:order'] ?? []

    return {
        ...uiSchema,
        'ui:order': [...incompleteOrder, '*', 'floor_level', 'location', 'secondary_location', 'ignore_for_report'],
        floor_level: {
            'ui:title': 'Floor Level',
            'ui:placeholder': 'Select Floor Level',
            'ui:field': 'SectionDivider',
            'ui:options': {
                dropdownWithPlaceholder: true,
                topDividerFieldType: 'dropdown',
                label: 'Sub Region',
            },
        },
        location: {
            'ui:placeholder': 'Select Location',
        },
        secondary_location: {
            'ui:placeholder': 'Select Location',
        },
        ignore_for_report: {
            'ui:title': 'Add to the material list?',
            'ui:field': 'SectionDivider',
            'ui:options': {
                topDividerFieldType: 'radio',
                inline: true,
            },
        },
    }
}

export function* getFormByType(
    action:
        | ReturnType<typeof setActiveDrawableGroup>
        | ReturnType<typeof changeDrawableGroupType>
        | ReturnType<typeof setActiveToolObjectId>
) {
    try {
        if (action.type === setActiveToolObjectId.type) {
            if (isNull(action.payload)) return
            const manager: PaperManager = yield call(managers.get2DManager)

            if (!manager) return

            const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

            const toolItem: paper.PathItem = yield call(workspaceTool.getItemWithPaperId, action.payload as number)

            const currentToolObject: IToolObject = yield select(selectActiveToolObject)

            // measure tool do not have form
            if (currentToolObject?.type === TOOL_TYPE_ENUMS.MEASUREMENT) return

            const currentFormType = currentToolObject?.type || toolItem?.data?.shapeType

            if (currentFormType) {
                yield fork(getAndActivateFormByType, currentFormType, FormTypes.tools)

                return
            }
        }

        const drawableGroup =
            action.type === SET_ACTIVE_DRAWABLE_GROUP
                ? (action as ReturnType<typeof setActiveDrawableGroup>).payload.activeDrawableGroup
                : (action as ReturnType<typeof changeDrawableGroupType>).payload.drawableGroup

        // do nothing if an active drawable group is null
        if (isNull(drawableGroup) || (isArray(drawableGroup) && drawableGroup.length === 0)) {
            yield put(activateForm(''))

            return
        }

        const { openings } = drawableGroup

        yield fork(getAndActivateFormByType, drawableGroup.type, FormTypes.materials, openings)
    } catch (error) {
        yield put(formError((error as any).message))
    }
}

function createWallSchema(schema: JSONSchema7, isWallSheathingEnabled: boolean, activeBuilding: Building | undefined) {
    if (!isWallSheathingEnabled) {
        return schema
    }
    if (!activeBuilding?.settings?.sheathing?.continuous_sheathing) {
        const allOf = schema.allOf?.filter((a) => {
            if (JSON.stringify(a) !== JSON.stringify(floorDepthCondition)) {
                return a
            }
        })

        return {
            ...schema,
            allOf: allOf,
        }
    }
    const hasFloorDepth = schema.allOf?.some((a) => {
        if (JSON.stringify(a) === JSON.stringify(floorDepthCondition)) {
            return true
        }
    })

    if (!hasFloorDepth) {
        return { ...schema, allOf: [...schema.allOf!, floorDepthCondition] }
    }

    return schema
}

export function* getAndActivateFormByType(type: DRAWABLE_TYPES | TOOL_TYPE_ENUMS, formType: FormTypes, openings?) {
    try {
        // clear the redux forms slice error state
        const activeBuilding: Building | undefined = yield select(selectActiveBuildingFromActiveFloor)

        yield put(clearFormErrors())
        // get the form cache from redux
        const formCache: FormsState['formCache'] = yield select(selectFormCache)

        // get the base form by drawable group type
        const form: FormProperties | undefined = get(materialToFormMap, type)

        const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)

        const azureFeatureFlags: AzureFeatureFlag[] = yield select(selectAzureFeatureFlags)

        const isWallSheathingEnabled = isAzureFeatureFlagEnabled(azureFeatureFlags, AzureFeatureFlagIds.wall_sheathing)

        let building_id = GENERAL_CACHE
        // use GENERAL_CACHE for tool objects and use building_id for others

        if (!(type in TOOL_TYPE_ENUMS) && activeFloor?.building_id) {
            building_id = activeFloor?.building_id?.toString()
        }

        // if form cache has the form, set it to be the active form
        const buildingFormCache = formCache?.[building_id]

        if (has(buildingFormCache, type)) {
            // ensure that WALL type form schema has the correct default for king stud,
            // based on the length of opening_locations for an active drawable group
            if (type === DRAWABLE_TYPES.WALL) {
                const { schema, uiSchema } = buildingFormCache[type]
                const jsonSchema = createWallSchema(schema as JSONSchema7, isWallSheathingEnabled, activeBuilding)

                yield put(
                    cacheForm({
                        form: {
                            schema: {
                                ...jsonSchema,
                                ...(openings
                                    ? {
                                          definitions: {
                                              ...jsonSchema.definitions,
                                              is_king_stud: {
                                                  type: 'boolean',
                                                  default: openings.some(
                                                      (opening) => opening.opening_locations.length > 1
                                                  ),
                                              },
                                          },
                                      }
                                    : {}),
                            },
                            uiSchema,
                        },
                        type,
                        buildingId: building_id,
                    })
                )
            }

            yield put(activateForm(type))

            return
        }

        const completeSchema = yield call(getFormSchema, type, building_id)

        let completeUISchema: UiSchema = form.uiSchema

        // in case it's drawable type, add rest of UI schema
        if (formType === FormTypes.materials) {
            completeUISchema = yield call(
                schemasWithNewLocationModule.includes(type)
                    ? addLocationModuleToUISchema
                    : addBundleAndToggleToUISchema,
                prepareUiSchemaBasedOnAzureFeatureFlag(completeUISchema, azureFeatureFlags)
            )

            // add Advanced Settings To Schema
            completeUISchema = yield call(
                addAdvancedSettingsToSchemaByType,
                completeUISchema,
                type,
                azureFeatureFlags,
                completeSchema
            )
        }

        // cache the new form, fields, and activate the form for use
        yield put(
            cacheForm({
                form: {
                    schema:
                        type === DRAWABLE_TYPES.WALL
                            ? createWallSchema(
                                  omit(completeSchema, 'description'),
                                  isWallSheathingEnabled,
                                  activeBuilding
                              )
                            : omit(completeSchema, 'description'),
                    uiSchema: completeUISchema,
                },
                type,
                buildingId: building_id,
            })
        )
        yield put(activateForm(type))
    } catch (error) {
        yield put(formError((error as any).message))
    }
}

export function* watchForMaterialFormChange(): Generator<StrictEffect, void, unknown> {
    yield takeLatest([SET_ACTIVE_DRAWABLE_GROUP, CHANGE_DRAWABLE_GROUP_TYPE, setActiveToolObjectId.type], getFormByType)
}

export function* handleRimboardFormLoad() {
    // Auto-detect floor_level from region or page name BEFORE activating the form
    const manager: PaperManager = yield call(managers.get2DManager)

    if (manager) {
        const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)
        const rimboardPendingBorderIds: number[] | null = yield select(selectRimboardPendingBorderIds)
        const firstItem: paper.Path | null = rimboardPendingBorderIds?.length
            ? yield call(workspaceTool.getItemWithPaperId, rimboardPendingBorderIds[0])
            : null

        // Look up the parent floor/roof system settings (floor_level, location, secondary_location)
        const geometries: Geometry[] = yield select(selectGeometries)
        const parentSettings =
            firstItem && geometries.length
                ? getClosestFloorRoofSystemSettings(firstItem, geometries, workspaceTool)
                : null

        const parentFloorLevel = parentSettings?.floor_level
        const parentLocation = parentSettings?.location
        const parentSecondaryLocation = parentSettings?.secondary_location

        yield put(
            setRimboardParentSettings(
                parentFloorLevel || parentLocation || parentSecondaryLocation
                    ? {
                          floor_level: parentFloorLevel,
                          location: parentLocation,
                          secondary_location: parentSecondaryLocation,
                      }
                    : null
            )
        )

        // Rimboard floor_level always comes from the parent floor/roof system
        yield put(setRimboardAutoFloorLevel(parentFloorLevel ?? null))
    } else {
        yield put(setRimboardAutoFloorLevel(null))
        yield put(setRimboardParentSettings(null))
    }

    yield fork(getAndActivateFormByType, DRAWABLE_TYPES.RIMBOARD, FormTypes.materials)
}

export function* watchForRimboardFormLoad(): Generator<StrictEffect, void, unknown> {
    yield takeLatest(setPendingRimboardBorders.type, handleRimboardFormLoad)
}
